import { Translation } from "../models/templates";

// Matches standalone `t("key")` / `t('key')` calls, excluding `foo.t("key")`.
// Mirrors lib/automation/v3_translations.py on the backend.
const T_CALL_PATTERN = /(?<![\w.])t\(\s*(?:'([^']*)'|"([^"]*)")\s*\)/g;

export const extractTranslationKeys = (sources: (string | null | undefined)[]): string[] => {
  const keys = new Set<string>();
  for (const source of sources) {
    if (!source) continue;
    for (const match of source.matchAll(T_CALL_PATTERN)) {
      keys.add(match[1] ?? match[2] ?? "");
    }
  }
  keys.delete("");
  return Array.from(keys).sort();
};

// Namespaces a V3 flow's keys by its automation path ("EVENT.ACTION.TARGET"); a shared
// TEMPLATE/MACRO/BLOCK uses its own target name instead. Mirrors lib/automation/v3_translations.py.
export const buildV3TranslationKeyPrefix = (
  event: string | null | undefined,
  action: string | null | undefined,
  target: string | null | undefined,
): string => {
  if (!event || !action || !target) return "";
  if (event === "replacements" && action === "blocks") return target;
  return `${event}.${action}.${target}`;
};

export const qualifyTranslationKey = (bareKey: string, prefix: string): string =>
  prefix ? `${prefix}.${bareKey}` : bareKey;

// Get the most relevant translation for the current path (the lower in the chain, the more generic the template will be).
export const getTranslationForKey = (
  allTranslations: { [key: string]: Array<Translation> },
  selectedKey: string,
  currentVersion: string
): Translation | null => {
  let allTranslationsForThisKey = selectedKey in allTranslations ? [...allTranslations[selectedKey]] : [];
  if (!selectedKey || !allTranslationsForThisKey) {
    return null;
  }

  let calculatedTranslation = null;

  // If we're on live, filter out the draft of the top level. Always filter out all drafts for all other levels.
  // If we ever decide we want to show draft translations on lower levels, there's work to be done here.
  allTranslationsForThisKey = allTranslationsForThisKey.filter(
    (t) =>
      (t.key === selectedKey && t.version === "live") ||
      (t.key === selectedKey && t.version === currentVersion) ||
      (t.key !== selectedKey && t.version === "live") // only live versions for translations lower down the chain
  );

  // Find the translation for the current key and language
  for (calculatedTranslation of allTranslationsForThisKey) {
    if (
      calculatedTranslation.value != null &&
      Object.keys(calculatedTranslation.value).length > 0 &&
      !Object.values(calculatedTranslation.value).every((v) => v === null)
    ) {
      return calculatedTranslation;
    }
  }

  return null;
};
